<template>
  <div ref="rootEl">

    <!-- ─────────────────────────────────────────────────────────────
         CHATBOT AI SECTION
    ───────────────────────────────────────────────────────────────── -->
    <section
      id="chatbots"
      class="py-28 bg-white dark:bg-dark overflow-hidden transition-colors duration-300"
      data-animate="chatbot"
      :class="{ 'is-visible': visible.chatbot }"
    >
      <div class="container mx-auto px-4">
        <div class="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">

          <!-- Left: Animated chat mockup -->
          <div class="anim-el d1 relative">
            <!-- Ambient glow -->
            <div class="absolute -inset-10 pointer-events-none">
              <div class="w-64 h-64 rounded-full bg-blue-500/[0.05] blur-3xl absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"></div>
            </div>

            <div class="relative rounded-2xl border border-gray-200 dark:border-white/[0.08] overflow-hidden shadow-xl bg-white dark:bg-dark transition-colors duration-300">
              <!-- Chat header -->
              <div class="flex items-center gap-3 px-4 py-3 bg-gray-50 dark:bg-white/[0.04] border-b border-gray-200 dark:border-white/[0.06]">
                <div class="w-8 h-8 rounded-full bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
                  <Bot class="w-4 h-4 text-blue-500" />
                </div>
                <div>
                  <p class="text-[12px] font-semibold text-gray-900 dark:text-white">Assistente AI</p>
                  <div class="flex items-center gap-1.5">
                    <span class="w-1.5 h-1.5 rounded-full bg-green-500 inline-block"></span>
                    <span class="text-[10px] text-gray-400 dark:text-gray-500">Online · risponde in &lt;1s</span>
                  </div>
                </div>
                <div class="ml-auto flex gap-1">
                  <div class="w-2 h-2 rounded-full bg-gray-200 dark:bg-white/[0.1]"></div>
                  <div class="w-2 h-2 rounded-full bg-gray-200 dark:bg-white/[0.1]"></div>
                  <div class="w-2 h-2 rounded-full bg-gray-200 dark:bg-white/[0.1]"></div>
                </div>
              </div>

              <!-- Chat messages -->
              <div class="p-4 space-y-3 min-h-[240px]">
                <!-- User msg 1 -->
                <transition name="msg-in">
                  <div v-if="chatStage >= 1" class="flex justify-end">
                    <div class="max-w-[75%] rounded-xl rounded-tr-sm bg-blue-500 px-3.5 py-2.5">
                      <p class="text-[12px] text-white leading-snug">Ciao! Ho bisogno di assistenza per il mio ordine #4821</p>
                    </div>
                  </div>
                </transition>

                <!-- Bot typing -->
                <transition name="msg-in">
                  <div v-if="chatStage === 2" class="flex justify-start">
                    <div class="flex items-center gap-1.5 rounded-xl rounded-tl-sm bg-gray-100 dark:bg-white/[0.06] border border-gray-200 dark:border-white/[0.06] px-4 py-3">
                      <span class="typing-dot"></span>
                      <span class="typing-dot" style="animation-delay:0.2s"></span>
                      <span class="typing-dot" style="animation-delay:0.4s"></span>
                    </div>
                  </div>
                </transition>

                <!-- Bot msg 1 -->
                <transition name="msg-in">
                  <div v-if="chatStage >= 3" class="flex justify-start">
                    <div class="max-w-[80%] rounded-xl rounded-tl-sm bg-gray-100 dark:bg-white/[0.06] border border-gray-200 dark:border-white/[0.06] px-3.5 py-2.5">
                      <p class="text-[12px] text-gray-700 dark:text-gray-200 leading-snug">Il tuo ordine #4821 è in spedizione! 📦 Arrivo previsto: <strong>domani 10-12</strong>. Vuoi ricevere aggiornamenti via SMS?</p>
                    </div>
                  </div>
                </transition>

                <!-- User msg 2 -->
                <transition name="msg-in">
                  <div v-if="chatStage >= 4" class="flex justify-end">
                    <div class="max-w-[65%] rounded-xl rounded-tr-sm bg-blue-500 px-3.5 py-2.5">
                      <p class="text-[12px] text-white leading-snug">Sì, perfetto grazie!</p>
                    </div>
                  </div>
                </transition>

                <!-- Bot typing 2 -->
                <transition name="msg-in">
                  <div v-if="chatStage === 5" class="flex justify-start">
                    <div class="flex items-center gap-1.5 rounded-xl rounded-tl-sm bg-gray-100 dark:bg-white/[0.06] border border-gray-200 dark:border-white/[0.06] px-4 py-3">
                      <span class="typing-dot"></span>
                      <span class="typing-dot" style="animation-delay:0.2s"></span>
                      <span class="typing-dot" style="animation-delay:0.4s"></span>
                    </div>
                  </div>
                </transition>

                <!-- Bot msg 2 -->
                <transition name="msg-in">
                  <div v-if="chatStage >= 6" class="flex justify-start">
                    <div class="max-w-[80%] rounded-xl rounded-tl-sm bg-gray-100 dark:bg-white/[0.06] border border-gray-200 dark:border-white/[0.06] px-3.5 py-2.5">
                      <p class="text-[12px] text-gray-700 dark:text-gray-200 leading-snug">Notifiche SMS attivate! 😊 Posso aiutarti con altro?</p>
                    </div>
                  </div>
                </transition>
              </div>

              <!-- Stats strip -->
              <div class="grid grid-cols-3 divide-x divide-gray-200 dark:divide-white/[0.06] border-t border-gray-200 dark:border-white/[0.06]">
                <div class="p-3 text-center">
                  <p class="text-[10px] text-gray-400 dark:text-gray-500 mb-0.5">Risposta</p>
                  <p class="text-sm font-bold text-gray-900 dark:text-white">&lt;1s</p>
                </div>
                <div class="p-3 text-center">
                  <p class="text-[10px] text-gray-400 dark:text-gray-500 mb-0.5">Disponibile</p>
                  <p class="text-sm font-bold text-gray-900 dark:text-white">24/7</p>
                </div>
                <div class="p-3 text-center">
                  <p class="text-[10px] text-gray-400 dark:text-gray-500 mb-0.5">Soddisfazione</p>
                  <p class="text-sm font-bold text-blue-600 dark:text-blue-400">94%</p>
                </div>
              </div>
            </div>

            <!-- Floating badges -->
            <div class="absolute -top-4 -right-4 badge-float fb1 hidden xl:block">
              <div class="rounded-xl bg-white dark:bg-dark border border-gray-200 dark:border-white/[0.08] shadow-lg px-3 py-2 flex items-center gap-2">
                <div class="w-7 h-7 rounded-full bg-blue-500/10 flex items-center justify-center">
                  <MessageSquare class="w-3.5 h-3.5 text-blue-500" />
                </div>
                <div>
                  <div class="text-[10px] text-gray-400 dark:text-gray-500">Conversazioni/mese</div>
                  <div class="text-xs font-bold text-gray-900 dark:text-white">12,400+</div>
                </div>
              </div>
            </div>

            <div class="absolute -bottom-3 -left-4 badge-float fb2 hidden xl:block">
              <div class="rounded-xl bg-white dark:bg-dark border border-gray-200 dark:border-white/[0.08] shadow-lg px-3 py-2 flex items-center gap-2">
                <div class="w-7 h-7 rounded-full bg-green-500/10 flex items-center justify-center">
                  <Globe class="w-3.5 h-3.5 text-green-500" />
                </div>
                <div>
                  <div class="text-[10px] text-gray-400 dark:text-gray-500">Lingue supportate</div>
                  <div class="text-xs font-bold text-gray-900 dark:text-white">IT · DE · EN · FR</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Right: Text content -->
          <div class="anim-el d2">
            <div class="w-11 h-11 rounded-xl bg-blue-500/10 flex items-center justify-center mb-5">
              <Bot class="h-5 w-5 text-blue-500" />
            </div>
            <p class="text-[13px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600 font-medium mb-4">
              Chatbot AI
            </p>
            <h2 class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white tracking-tight leading-[1.1] mb-4 transition-colors duration-300">
              {{ t('solutions.chatbots.title') }}<br />
              <span class="gradient-text">{{ t('solutions.chatbots.highlight') }}</span>
            </h2>
            <p class="text-lg text-gray-500 dark:text-gray-400 leading-relaxed mb-8 transition-colors duration-300">
              {{ t('solutions.chatbots.description') }}
            </p>

            <ul class="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-3 mb-8">
              <li v-for="feature in chatbotFeatures" :key="feature" class="flex items-start gap-3">
                <Check class="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
                <span class="text-gray-600 dark:text-gray-300 text-[15px] transition-colors duration-300">{{ feature }}</span>
              </li>
            </ul>

            <!-- Use-case cards -->
            <div class="grid grid-cols-2 gap-3 mb-8">
              <div
                v-for="card in chatbotCards"
                :key="card.title"
                class="rounded-xl bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] p-4 transition-all duration-300 hover:-translate-y-1 hover:border-blue-200 dark:hover:border-blue-500/20 hover:shadow-md hover:shadow-blue-500/5"
              >
                <component :is="card.icon" class="h-4 w-4 text-blue-500 mb-2" />
                <h3 class="font-semibold text-gray-900 dark:text-white text-[13px] mb-1 transition-colors duration-300">{{ card.title }}</h3>
                <p class="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">{{ card.description }}</p>
              </div>
            </div>

            <router-link
              to="/chatbot"
              class="inline-flex items-center h-11 px-6 bg-gray-900 dark:bg-white text-white dark:text-dark font-semibold text-[14px] rounded-xl hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors group"
            >
              {{ t('solutions.chatbots.learnMore') }}
              <ArrowRight class="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </router-link>
          </div>

        </div>
      </div>
    </section>

    <!-- ─────────────────────────────────────────────────────────────
         AUTOMAZIONI AI SECTION
    ───────────────────────────────────────────────────────────────── -->
    <section
      id="automations"
      class="py-24 bg-gray-50 dark:bg-dark transition-colors duration-300"
      data-animate="automations"
      :class="{ 'is-visible': visible.automations }"
    >
      <div class="container mx-auto px-4">
        <div class="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">

          <!-- Left: Content -->
          <div class="anim-el d1">
            <div class="w-11 h-11 rounded-xl bg-blue-500/10 flex items-center justify-center mb-5">
              <Sparkles class="h-5 w-5 text-blue-500" />
            </div>
            <p class="text-[13px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600 font-medium mb-4">
              Automazioni AI
            </p>
            <h2 class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white tracking-tight leading-[1.1] mb-4 transition-colors duration-300">
              Automatizza i processi.<br />
              <span class="gradient-text">Scala il business.</span>
            </h2>
            <p class="text-lg text-gray-500 dark:text-gray-400 leading-relaxed mb-8 transition-colors duration-300">
              Chatbot AI, agenti vocali, workflow automation e marketing intelligente.
              Un ecosistema integrato che lavora 24/7, riducendo i costi operativi e
              moltiplicando la capacità produttiva della tua azienda.
            </p>

            <div class="grid grid-cols-2 gap-3 mb-8">
              <div
                v-for="svc in automationServices"
                :key="svc.title"
                class="rounded-xl border border-gray-200 dark:border-white/[0.06] bg-white dark:bg-white/[0.03] p-4 transition-colors duration-300"
              >
                <component :is="svc.icon" class="h-4 w-4 text-blue-500 mb-2" />
                <p class="text-sm font-semibold text-gray-900 dark:text-white mb-1">{{ svc.title }}</p>
                <p class="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">{{ svc.desc }}</p>
              </div>
            </div>

            <router-link
              to="/automazioni-ai"
              class="inline-flex items-center h-11 px-6 bg-gray-900 dark:bg-white text-white dark:text-dark font-semibold text-[14px] rounded-xl hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors group"
            >
              Scopri le automazioni AI
              <ArrowRight class="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </router-link>
          </div>

          <!-- Right: Animated dashboard mockup -->
          <div class="anim-el d2">
            <div class="rounded-2xl border border-gray-200 dark:border-white/[0.08] overflow-hidden shadow-xl bg-white dark:bg-dark transition-colors duration-300">
              <!-- Browser chrome -->
              <div class="flex items-center gap-2 px-4 py-2.5 bg-gray-50 dark:bg-white/[0.04] border-b border-gray-200 dark:border-white/[0.06]">
                <div class="flex gap-1.5">
                  <div class="w-2.5 h-2.5 rounded-full bg-red-400/80"></div>
                  <div class="w-2.5 h-2.5 rounded-full bg-yellow-400/80"></div>
                  <div class="w-2.5 h-2.5 rounded-full bg-green-400/80"></div>
                </div>
                <div class="flex-1 h-6 rounded-md bg-white dark:bg-white/[0.06] mx-8 flex items-center px-3">
                  <Lock class="w-3 h-3 text-green-500 mr-1.5" />
                  <span class="text-[11px] text-gray-400 dark:text-gray-500">automation.ciobai.ch</span>
                </div>
              </div>

              <div class="p-5 space-y-4">
                <!-- Stats row -->
                <div class="grid grid-cols-3 gap-3">
                  <div class="rounded-lg border border-gray-200 dark:border-white/[0.06] bg-gray-50 dark:bg-white/[0.03] p-3 text-center">
                    <p class="text-[10px] text-gray-400 dark:text-gray-500 mb-1">Task/giorno</p>
                    <p class="text-xl font-bold text-gray-900 dark:text-white tabular-nums">{{ cTasks }}</p>
                    <p class="text-[10px] text-green-500 flex items-center justify-center gap-0.5 mt-0.5">
                      <TrendingUp class="h-2.5 w-2.5" />+12%
                    </p>
                  </div>
                  <div class="rounded-lg border border-gray-200 dark:border-white/[0.06] bg-gray-50 dark:bg-white/[0.03] p-3 text-center">
                    <p class="text-[10px] text-gray-400 dark:text-gray-500 mb-1">Ore risparmiate</p>
                    <p class="text-xl font-bold text-gray-900 dark:text-white tabular-nums">{{ cHours }}</p>
                    <p class="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">questo mese</p>
                  </div>
                  <div class="rounded-lg border border-gray-200 dark:border-white/[0.06] bg-gray-50 dark:bg-white/[0.03] p-3 text-center">
                    <p class="text-[10px] text-gray-400 dark:text-gray-500 mb-1">Processi attivi</p>
                    <p class="text-xl font-bold text-blue-600 dark:text-blue-400 tabular-nums">{{ cProcs }}</p>
                    <p class="text-[10px] text-blue-500 mt-0.5">live</p>
                  </div>
                </div>

                <!-- Active pipeline -->
                <div class="rounded-lg border border-gray-200 dark:border-white/[0.06] bg-gray-50 dark:bg-white/[0.03] p-3">
                  <p class="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-3">Pipeline attiva</p>
                  <div class="space-y-2">
                    <div v-for="(step, i) in pipelineSteps" :key="step.label" class="flex items-center gap-2">
                      <div
                        class="w-5 h-5 rounded-md flex items-center justify-center text-[9px] font-bold flex-shrink-0 transition-colors duration-500"
                        :class="i < activePipeline ? 'bg-blue-500 text-white' : 'bg-gray-200 dark:bg-white/[0.08] text-gray-500'"
                      >{{ i + 1 }}</div>
                      <div class="flex-1 h-1.5 rounded-full bg-gray-200 dark:bg-white/[0.08] overflow-hidden">
                        <div
                          class="h-full rounded-full bg-blue-500 transition-all duration-700"
                          :style="{ width: i < activePipeline ? '100%' : '0%' }"
                        ></div>
                      </div>
                      <span class="text-[10px] text-gray-500 dark:text-gray-400 w-16 text-right shrink-0">{{ step.label }}</span>
                    </div>
                  </div>
                </div>

                <!-- Recent activity -->
                <div class="rounded-lg border border-gray-200 dark:border-white/[0.06] bg-gray-50 dark:bg-white/[0.03] p-3">
                  <p class="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-2">Attività recente</p>
                  <div class="space-y-2">
                    <div v-for="(act, i) in recentActivity" :key="i" class="flex items-center gap-2">
                      <div class="w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0 animate-pulse"></div>
                      <span class="text-[10px] text-gray-600 dark:text-gray-300 flex-1">{{ act.text }}</span>
                      <span class="text-[10px] text-gray-400 dark:text-gray-500 flex-shrink-0">{{ act.time }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>

  </div>
</template>

<script setup>
import {
  Bot, Check, ArrowRight, Lock, TrendingUp, Sparkles,
  PhoneCall, GitBranch, Target, MessageSquare, Globe,
  HeadphonesIcon, ShoppingCart, BookOpen, Presentation
} from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'
import { computed, ref, reactive, onMounted, onUnmounted, watch } from 'vue'

const { t, tm } = useI18n()
const rootEl = ref(null)
const visible = reactive({ chatbot: false, automations: false })
let observer = null

// Chat animation
const chatStage = ref(0)

// Automazioni counters
const cTasks = ref(0)
const cHours = ref(0)
const cProcs = ref(0)
const activePipeline = ref(0)

const pipelineSteps = [
  { label: 'Capture' },
  { label: 'Classify' },
  { label: 'Route' },
  { label: 'Execute' }
]

const recentActivity = [
  { text: 'Lead qualificato e inoltrato al CRM', time: '2s fa' },
  { text: 'Email di follow-up inviata automaticamente', time: '14s fa' },
  { text: 'Documento elaborato e archiviato', time: '1m fa' }
]

const automationServices = [
  { icon: Bot, title: 'Chatbot AI', desc: 'Supporto clienti e vendite automatizzate 24/7.' },
  { icon: PhoneCall, title: 'Agenti Vocali', desc: 'Centralino intelligente inbound e outbound.' },
  { icon: GitBranch, title: 'Workflow Automation', desc: 'Flussi documentali, scheduling e reporting.' },
  { icon: Target, title: 'Marketing AI', desc: 'Campagne che si ottimizzano in automatico.' }
]

function countUp(target, end, duration) {
  const start = performance.now()
  function tick(now) {
    const p = Math.min((now - start) / duration, 1)
    const eased = 1 - Math.pow(1 - p, 3)
    target.value = Math.round(eased * end)
    if (p < 1) requestAnimationFrame(tick)
  }
  requestAnimationFrame(tick)
}

watch(() => visible.chatbot, (v) => {
  if (!v) return
  chatStage.value = 0
  ;[300, 900, 1700, 2500, 3300, 4100].forEach((delay, i) => {
    setTimeout(() => { chatStage.value = i + 1 }, delay)
  })
})

watch(() => visible.automations, (v) => {
  if (!v) return
  countUp(cTasks, 847, 1800)
  countUp(cHours, 312, 1800)
  countUp(cProcs, 47, 1400)
  activePipeline.value = 0
  ;[400, 900, 1400, 1900].forEach((delay, i) => {
    setTimeout(() => { activePipeline.value = i + 1 }, delay)
  })
})

onMounted(() => {
  observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) visible[entry.target.dataset.animate] = true
    })
  }, { threshold: 0.05, rootMargin: '0px 0px -20px 0px' })
  if (rootEl.value) {
    rootEl.value.querySelectorAll('[data-animate]').forEach(el => observer.observe(el))
  }
})

onUnmounted(() => {
  if (observer) observer.disconnect()
})

const chatbotCards = computed(() => [
  { icon: HeadphonesIcon, title: t('solutions.cards.chatbot.customerService.title'), description: t('solutions.cards.chatbot.customerService.description') },
  { icon: ShoppingCart, title: t('solutions.cards.chatbot.salesAssistant.title'), description: t('solutions.cards.chatbot.salesAssistant.description') },
  { icon: BookOpen, title: t('solutions.cards.chatbot.informational.title'), description: t('solutions.cards.chatbot.informational.description') },
  { icon: Presentation, title: t('solutions.cards.chatbot.tutor.title'), description: t('solutions.cards.chatbot.tutor.description') }
])

const chatbotFeatures = computed(() => {
  const features = tm('solutions.chatbots.features')
  return Array.isArray(features) ? features : []
})
</script>

<style scoped>
.gradient-text {
  background: linear-gradient(135deg, #60a5fa 0%, #3b82f6 50%, #2563eb 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.tabular-nums { font-variant-numeric: tabular-nums; }

/* Scroll-triggered animation */
.anim-el {
  opacity: 0;
  transform: translateY(24px);
  transition: opacity 0.7s cubic-bezier(.22,1,.36,1), transform 0.7s cubic-bezier(.22,1,.36,1);
}
.is-visible .anim-el { opacity: 1; transform: translateY(0); }
.d1 { transition-delay: 0.05s; }
.d2 { transition-delay: 0.18s; }

/* Chat message entrance animation */
.msg-in-enter-active { transition: opacity 0.35s ease, transform 0.35s cubic-bezier(.22,1,.36,1); }
.msg-in-enter-from { opacity: 0; transform: translateY(10px); }
.msg-in-leave-active { transition: opacity 0.2s ease; }
.msg-in-leave-to { opacity: 0; }

/* Typing indicator dots */
.typing-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  color: #9ca3af;
  animation: typingDot 1.2s ease-in-out infinite;
}
@keyframes typingDot {
  0%, 60%, 100% { opacity: 0.3; transform: scale(1); }
  30% { opacity: 1; transform: scale(1.3); }
}

/* Floating badges */
.badge-float { animation: floatBadge 3.5s ease-in-out infinite; }
.fb1 { animation-delay: 0s; }
.fb2 { animation-delay: 1.4s; }
@keyframes floatBadge {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-7px); }
}
</style>
